// src/pages/TrendsPage.js

import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { fetchUserProfile } from "../services/auth";
import TopMenuBar from "../layouts/TopMenuBar";
import { useMusic } from "../context/MusicContext";
import { useProfile } from "../context/ProfileContext";
import { useSwipeable } from "react-swipeable";
import useSongActions from "../hooks/useSongActions";
import useCreditBubbleAnimation from "../hooks/useCreditBubbleAnimation";
import TrendsCarousel from "../components/trends/TrendsCarousel";
import TrendsMenu from "../components/trends/TrendsMenu";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import SwipeIntroModal from "../components/trends/SwipeIntroModal";
import DailyCapModal from "../components/trends/DailyCapModal";
import ArtistInfoCard from "../components/trends/ArtistInfoCard";
import { useNavigate } from "react-router-dom";
import controlsIcon from "../assets/controls.png";

const TrendsPage = ({ onOpenPlaylistModal }) => {
  // --- CONTEXT HOOKS ---
  const { 
    currentSong,
    previousSong,
    nextSong,
    isPlaying,
    playSource,
    playNext,
    playPrevious,
    trendsQueue,          // The visual queue of IDs
    trendsCurrentIndex,   // The index for the visual queue
    hydratedSongs,        // The cache of hydrated song objects
    trendsIsLoading,      // The new primary loading stateT
    startTrendsPlayback,
    incrementRatingCount, 
    togglePlayPause,
    hasDismissedTrendsIntro, 
    dismissTrendsIntro,
  } = useMusic();

  // --- LOCAL UI STATE & HOOKS ---
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate(); 
  const [creditBalance, setCreditBalance] = useState(100000); // Or from profile context

  const {
    trigger: triggerCreditAnimation,
    CreditAnimationOverlay,
    originRef,
    targetRef,
    isTargetHitting,
  } = useCreditBubbleAnimation();
  
  const { isDarkMode, canEarnPoints, pointsToAward } = useProfile();
  const [isIntroOpen, setIsIntroOpen] = useState(!hasDismissedTrendsIntro);
  const [isCapModalOpen, setIsCapModalOpen] = useState(false);

  const handleCloseIntro = () => {
    // Only trigger the "Power On" sequence if the user hasn't 
    // officially started their trends session yet
    if (!hasDismissedTrendsIntro) {
        if (playSource !== 'Trends' && trendsQueue.length > 0) {
        startTrendsPlayback(); 
        } else if (!isPlaying) {
        togglePlayPause();
        }
        dismissTrendsIntro(); // This marks the global flag as true
    }
    
    setIsIntroOpen(false); // This handles the local closing
    };

  // Use an effect to sync with the global state once it arrives
    useEffect(() => {
    if (hasDismissedTrendsIntro) {
        setIsIntroOpen(false);
    }
    }, [hasDismissedTrendsIntro]);

  const displayedSongId = trendsQueue[trendsCurrentIndex]?.id;
  const displayedSong = hydratedSongs?.[displayedSongId]
  
  const animationControls = useAnimation();

  const {
    rating,
    rateSong,
    isSaved,               // 👈 ADD THIS
    toggleSaved,           // 👈 ADD THIS
    isSubscribed,          // 👈 ADD THIS
    toggleSubscription,    // 👈 ADD THIS
    optimisticSetRating,              // 👈 Add this
    ratingFetched,                    // 👈 Add this
    // isSaved and toggleSaved are also available if you need them
  } = useSongActions({ // ✅ THIS IS THE FINAL CORRECT VERSION
    songId: currentSong?.id || displayedSongId,
    artistId: currentSong?.artistId || displayedSong?.artistId
  });
  
  // Effect for fetching the user's static profile (no change needed)
  useEffect(() => {
    const fetchProfile = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const profile = await fetchUserProfile(user.uid);
        setUserProfile(profile);
      }
    };
    fetchProfile();
  }, []);

  

  // This effect runs once when the page loads to check for a pending redirect.
  useEffect(() => {
      const redirectPath = sessionStorage.getItem('postAuthRedirect');
      if (redirectPath) {
          // If a path was saved, clear it and navigate the user there.
          sessionStorage.removeItem('postAuthRedirect');
          navigate(redirectPath);
      }
  }, [navigate]); // The dependency array ensures this only runs once.


  // ⚡ 0ms INSTANT OPTIMISTIC RATING HANDLER
  const handleRating = async (newRating) => {
  if (!currentSong && !displayedSong) return;

  const isFirstTime = !ratingFetched.current || !rating || rating === "unrated";

  // 1. Optimistic button scale animation
  optimisticSetRating(newRating);
  animationControls.start({ scale: [1, 2, 1], transition: { duration: 0.3 } });

  // 2. Launch 3D canvas flying bubbles
  // (The credit bubble hook triggers 'optimistic_points_earned' when particles land on target!)
  if (isFirstTime && canEarnPoints && triggerCreditAnimation) {
    triggerCreditAnimation(newRating, pointsToAward);
  }

  // 3. BACKGROUND NETWORK SYNC (Pass skipOptimisticEvent: true to prevent double/early trigger)
  try {
    const { showCapModal } = await rateSong(newRating, "trends", { skipOptimisticEvent: true });
    if (showCapModal) {
      setIsCapModalOpen(true);
    }
  } catch (err) {
    console.error("❌ Network sync error during rating:", err);
  }
};

  return (
        <div className="fixed inset-0 overflow-hidden bg-black selection:bg-none">
            <TopMenuBar 
                ref={targetRef} 
                userCredits={creditBalance} 
                isTargetHitting={isTargetHitting} 
            />

            <AnimatePresence>
                {isIntroOpen && ( // ✅ Remove "!hasDismissedTrendsIntro" from here
                    <SwipeIntroModal 
                    onClose={handleCloseIntro} 
                    isLoading={trendsIsLoading} 
                    />
                )}
            </AnimatePresence>

            {/* Daily Point Cap Notice Modal */}
            <DailyCapModal 
                isOpen={isCapModalOpen}
                onClose={() => setIsCapModalOpen(false)}
                isDarkMode={isDarkMode}
            />

            {!trendsIsLoading && (
                <>
                    {/* ✅ 3. Pass the new decoupled props to the carousel */}
                    <TrendsCarousel
                        queue={trendsQueue}
                        currentIndex={trendsCurrentIndex}
                        hydratedSongs={hydratedSongs}
                        onNext={playNext}
                        onPrevious={playPrevious}
                        onRate={handleRating}
                        isPlaying={isPlaying} // 👈 PASS the state
                        togglePlayPause={togglePlayPause}
                        playSource={playSource}
                        startTrendsPlayback={startTrendsPlayback}
                    />
                    {!isIntroOpen && (
                        <ArtistInfoCard currentSong={displayedSong} />
                    )}

                    {/* REOPEN BUTTON: Always available if the guide is hidden */}
                    {(!isIntroOpen && !trendsIsLoading) && (
                        <motion.button
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            onClick={() => setIsIntroOpen(true)}
                            className="fixed top-20 left-4 z-40 w-10 h-10 bg-black/40 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center shadow-lg"
                        >
                            <img src={controlsIcon} className="w-5 h-5 opacity-70" alt="Help" />
                        </motion.button>
                    )}
                </>
            )}

            {/* --- UI Overlays --- */}
            {!isIntroOpen && (
            <div className="relative h-full w-full pt-16 pointer-events-none z-40">
                {/* ✅ 4. Pass the single `displayedSong` object to child components */}
                <TrendsMenu
                    currentSong={displayedSong}
                    rating={rating}
                    animationControls={animationControls}
                    onOpenPlaylistModal={onOpenPlaylistModal}
                    isSubscribed={isSubscribed}
                    toggleSubscription={toggleSubscription}
                    onRate={handleRating}
                    originRef={originRef}
                />
            </div>
            )}

            {CreditAnimationOverlay}
        </div>
    );
};

export default TrendsPage;